// ============================================================================
// Facebook Page — getInsights capability method.
//
// Endpoint: GET `/{post-id}/insights?metric=post_media_view,
//                                          post_total_media_view_unique,
//                                          post_reactions_by_type_total,
//                                          post_clicks`
//
// The Graph API returns one row per metric, each with a `values[]` array
// holding the snapshot. We map those onto the flat `SocialPostInsights`
// shape consumed by the marketing module.
//
// Why not richer breakdowns: Meta's `?breakdown=` parameter requires
// elevated app review. For the v1 marketing dashboard we stick to the
// always-available post-level metrics; placement breakdowns can be added
// later via `SocialPlacementInsights[]`.
// ============================================================================

import type {
  SocialGetInsightsArgs,
  SocialPostInsights,
  SocialPostingCallCtx,
  SocialResult,
} from '../../capabilities/social-posting'
import type { RateLimitCheck } from './rate-limit-check'
import { errorFromResponse } from './errors'
import { FB_PAGE_GRAPH_API_BASE } from './oauth'
import type {
  FacebookPageEngagementTotalsResponse,
  FacebookPageCommentsResponse,
  FacebookPageInsightRow,
  FacebookPageInsightsResponse,
} from './types'

// ============================================================================
// Constants
// ============================================================================

const ENDPOINT_LABEL = 'facebook:page_insights'

/** Minimum-viable set of metrics for the v1 marketing dashboard. */
const DEFAULT_METRICS = [
  'post_media_view',
  'post_total_media_view_unique',
  'post_reactions_by_type_total',
  'post_clicks',
] as const

// ============================================================================
// getInsights
// ============================================================================

export interface GetInsightsDeps {
  fetchImpl?: typeof fetch
  graphApiBase?: string
  /** Override the metric list — useful in tests / advanced consumers. */
  metrics?: readonly string[]
  /**
   * Rate-limit check callback. When omitted, the adapter fail-opens (passes
   * every request through). The dispatcher / registration site wires a real
   * check that delegates to `services/rate-limiter.service`.
   */
  rateLimitCheck?: RateLimitCheck
}

export async function getInsights(
  args: SocialGetInsightsArgs,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  deps: GetInsightsDeps = {},
): Promise<SocialResult<SocialPostInsights>> {
  if (typeof pageAccessToken !== 'string' || pageAccessToken.length === 0) {
    return {
      ok: false,
      error: {
        code: 'auth',
        message: 'Facebook Page access token is missing',
        retryable: false,
      },
    }
  }
  if (typeof args.providerPostId !== 'string' || args.providerPostId.length === 0) {
    return {
      ok: false,
      error: {
        code: 'provider_rejected',
        message: 'providerPostId is required',
        retryable: false,
      },
    }
  }

  const gate = await checkRateLimit(ctx, ENDPOINT_LABEL, deps.rateLimitCheck)
  if (!gate.ok) return gate as SocialResult<SocialPostInsights>

  const fetchImpl = deps.fetchImpl ?? fetch
  const base = deps.graphApiBase ?? FB_PAGE_GRAPH_API_BASE
  const metrics = (deps.metrics && deps.metrics.length > 0 ? deps.metrics : DEFAULT_METRICS) as readonly string[]

  let response: Response
  try {
    response = await fetchInsightsResponse({
      providerPostId: args.providerPostId,
      metrics,
      pageAccessToken,
      fetchImpl,
      base,
    })
  } catch (err) {
    ctx.redactedLogger.error('getInsights network error', {
      error: err instanceof Error ? err.message : 'unknown',
    })
    return {
      ok: false,
      error: {
        code: 'transient',
        message: 'Network error talking to Facebook Graph',
        retryable: true,
      },
    }
  }

  if (!response.ok) {
    const err = await errorFromResponse(response, 'Facebook Graph insights failed')
    ctx.redactedLogger.warn('getInsights failed', {
      code: err.code,
      httpStatus: response.status,
      providerTraceId: err.providerTraceId,
    })

    const partialRows = await fetchMetricsIndividually({
      providerPostId: args.providerPostId,
      metrics,
      pageAccessToken,
      fetchImpl,
      base,
    })
    if (partialRows.length > 0) {
      const mapped = mapInsightsResponse(partialRows, args.providerPostId)
      const totals = await fetchEngagementTotals({
        providerPostId: args.providerPostId,
        pageAccessToken,
        fetchImpl,
        base,
      })
      return {
        ok: true,
        data: mergeInsightTotals(mapped, totals),
      }
    }

    const fallbackTotals = await fetchEngagementTotals({
      providerPostId: args.providerPostId,
      pageAccessToken,
      fetchImpl,
      base,
    })
    if (hasAnyEngagementMetric(fallbackTotals)) {
      return {
        ok: true,
        data: {
          providerPostId: args.providerPostId,
          ...fallbackTotals,
          snapshotAt: new Date().toISOString(),
        },
      }
    }
    return { ok: false, error: err }
  }

  const body = (await response.json().catch(() => null)) as FacebookPageInsightsResponse | null
  if (!body || !Array.isArray(body.data)) {
    return {
      ok: false,
      error: {
        code: 'unknown',
        message: 'Facebook insights returned no data array',
        retryable: false,
      },
    }
  }

  const mapped = mapInsightsResponse(body.data, args.providerPostId)
  const totals = await fetchEngagementTotals({
    providerPostId: args.providerPostId,
    pageAccessToken,
    fetchImpl,
    base,
  })

  return {
    ok: true,
    data: mergeInsightTotals(mapped, totals),
  }
}

function buildInsightsUrl(input: {
  providerPostId: string
  metrics: readonly string[]
  base: string
}): string {
  const params = new URLSearchParams({
    metric: input.metrics.join(','),
    period: 'lifetime',
  })
  return `${input.base}/${encodeURIComponent(input.providerPostId)}/insights?${params.toString()}`
}

async function fetchInsightsResponse(input: {
  providerPostId: string
  metrics: readonly string[]
  pageAccessToken: string
  fetchImpl: typeof fetch
  base: string
}): Promise<Response> {
  const fetchImpl = input.fetchImpl
  return fetchImpl(buildInsightsUrl(input), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${input.pageAccessToken}`,
    },
  })
}

async function fetchMetricsIndividually(input: {
  providerPostId: string
  metrics: readonly string[]
  pageAccessToken: string
  fetchImpl: typeof fetch
  base: string
}): Promise<FacebookPageInsightRow[]> {
  const rows: FacebookPageInsightRow[] = []
  for (const metric of input.metrics) {
    const response = await fetchInsightsResponse({
      ...input,
      metrics: [metric],
    })
    if (!response.ok) continue
    const body = (await response.json().catch(() => null)) as
      | FacebookPageInsightsResponse
      | null
    if (!body || !Array.isArray(body.data)) continue
    rows.push(...body.data)
  }
  return rows
}

async function fetchEngagementTotals(input: {
  providerPostId: string
  pageAccessToken: string
  fetchImpl: typeof fetch
  base: string
}): Promise<Pick<SocialPostInsights, 'comments' | 'shares' | 'likes'>> {
  const out: Pick<SocialPostInsights, 'comments' | 'shares' | 'likes'> = {}

  const engagementBody = await fetchPostFields(input, 'comments.limit(0).summary(true),shares')
  if (engagementBody) {
    if (typeof engagementBody.comments?.summary?.total_count === 'number') {
      out.comments = engagementBody.comments.summary.total_count
    }
    if (typeof engagementBody.shares?.count === 'number') {
      out.shares = engagementBody.shares.count
    }
  }

  const reactionsBody = await fetchPostFields(input, 'reactions.limit(0).summary(true),likes.limit(0).summary(true)')
  if (typeof reactionsBody?.reactions?.summary?.total_count === 'number') {
    out.likes = reactionsBody.reactions.summary.total_count
  } else if (typeof reactionsBody?.likes?.summary?.total_count === 'number') {
    out.likes = reactionsBody.likes.summary.total_count
  }

  if (typeof out.comments !== 'number') {
    const commentCount = await fetchCommentCountFallback(input)
    if (typeof commentCount === 'number') out.comments = commentCount
  }

  return out
}

function hasAnyEngagementMetric(
  metrics: Pick<SocialPostInsights, 'comments' | 'shares' | 'likes'>,
): boolean {
  return (
    typeof metrics.comments === 'number' ||
    typeof metrics.shares === 'number' ||
    typeof metrics.likes === 'number'
  )
}

async function fetchPostFields(
  input: {
    providerPostId: string
    pageAccessToken: string
    fetchImpl: typeof fetch
    base: string
  },
  fields: string,
): Promise<FacebookPageEngagementTotalsResponse | null> {
  const url =
    `${input.base}/${encodeURIComponent(input.providerPostId)}` +
    `?fields=${encodeURIComponent(fields)}`
  const fetchImpl = input.fetchImpl

  try {
    const response = await fetchImpl(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${input.pageAccessToken}`,
      },
    })
    if (!response.ok) return null
    return (await response.json().catch(() => null)) as
      | FacebookPageEngagementTotalsResponse
      | null
  } catch {
    return null
  }
}

function mergeInsightTotals(
  mapped: SocialPostInsights,
  totals: Pick<SocialPostInsights, 'comments' | 'shares' | 'likes'>,
): SocialPostInsights {
  const merged: SocialPostInsights = {
    ...mapped,
    ...totals,
  }
  const rateDenominator =
    typeof merged.impressions === 'number' && merged.impressions > 0
      ? merged.impressions
      : typeof merged.reach === 'number' && merged.reach > 0
        ? merged.reach
        : undefined
  if (typeof merged.engagementRate !== 'number' && rateDenominator) {
    const engagement = [merged.likes, merged.comments, merged.shares, merged.clicks]
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
      .reduce((sum, value) => sum + value, 0)
    if (engagement > 0) merged.engagementRate = engagement / rateDenominator
  }
  return merged
}

async function fetchCommentCountFallback(input: {
  providerPostId: string
  pageAccessToken: string
  fetchImpl: typeof fetch
  base: string
}): Promise<number | undefined> {
  const params = new URLSearchParams({
    fields: 'id',
    filter: 'stream',
    limit: '100',
  })
  const url = `${input.base}/${encodeURIComponent(input.providerPostId)}/comments?${params.toString()}`
  const fetchImpl = input.fetchImpl

  try {
    const response = await fetchImpl(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${input.pageAccessToken}`,
      },
    })
    if (!response.ok) return undefined
    const body = (await response.json().catch(() => null)) as
      | (FacebookPageCommentsResponse & { summary?: { total_count?: number } })
      | null
    if (typeof body?.summary?.total_count === 'number') return body.summary.total_count
    if (Array.isArray(body?.data)) return body.data.length
  } catch {
    return undefined
  }
  return undefined
}

// ============================================================================
// Mapping
// ============================================================================

function mapInsightsResponse(
  rows: FacebookPageInsightRow[],
  providerPostId: string,
): SocialPostInsights {
  const byName = new Map<string, FacebookPageInsightRow>()
  for (const row of rows) {
    if (typeof row.name === 'string' && row.name.length > 0) {
      byName.set(row.name, row)
    }
  }

  const impressions =
    numericMetric(byName.get('post_media_view')) ??
    numericMetric(byName.get('post_impressions'))
  const reach =
    numericMetric(byName.get('post_total_media_view_unique')) ??
    numericMetric(byName.get('post_impressions_unique'))
  const clicks = numericMetric(byName.get('post_clicks'))
  const engaged = numericMetric(byName.get('post_engaged_users'))

  // `post_reactions_by_type_total` returns an object whose keys are reaction
  // types (`like`, `love`, `wow`, ...). Sum into `likes` (canonical) and
  // surface the breakdown into `metadata` only if downstream wants it.
  const reactionsRow = byName.get('post_reactions_by_type_total')
  const likes = sumReactions(reactionsRow)

  // engagementRate: Meta does not surface a single rate; keep this aligned
  // with social tools that calculate engagement over views/impressions.
  let engagementRate: number | undefined
  const rateDenominator =
    typeof impressions === 'number' && impressions > 0
      ? impressions
      : typeof reach === 'number' && reach > 0
        ? reach
        : undefined
  if (typeof engaged === 'number' && rateDenominator) {
    engagementRate = engaged / rateDenominator
  }

  return {
    providerPostId,
    impressions,
    reach,
    likes,
    // comments + shares require separate fields; they're not part of the
    // default metric set in #788 — left undefined.
    clicks,
    engagementRate,
    snapshotAt: new Date().toISOString(),
  }
}

function numericMetric(row: FacebookPageInsightRow | undefined): number | undefined {
  if (!row || !Array.isArray(row.values) || row.values.length === 0) return undefined
  const v = row.values[0].value
  if (typeof v === 'number' && Number.isFinite(v)) return v
  return undefined
}

function sumReactions(row: FacebookPageInsightRow | undefined): number | undefined {
  if (!row || !Array.isArray(row.values) || row.values.length === 0) return undefined
  const v = row.values[0].value
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (v && typeof v === 'object') {
    let total = 0
    for (const n of Object.values(v as Record<string, number>)) {
      if (typeof n === 'number' && Number.isFinite(n)) total += n
    }
    return total
  }
  return undefined
}

// ============================================================================
// Rate-limiter gate
// ============================================================================

async function checkRateLimit(
  ctx: SocialPostingCallCtx,
  endpoint: string,
  rateLimitCheck: RateLimitCheck | undefined,
): Promise<SocialResult<true>> {
  if (!rateLimitCheck) return { ok: true, data: true }
  const decision = await rateLimitCheck({ instanceId: ctx.instanceId, endpoint }, ctx)
  if (decision.allowed) return { ok: true, data: true }
  return {
    ok: false,
    error: {
      code: 'rate_limit',
      message: 'Rate limit exceeded for facebook:page_insights',
      retryable: true,
      retryAfterSeconds: decision.retryAfterSeconds,
    },
  }
}
